import React, { useCallback, useEffect, useState } from "react";
import ClientLayout from "../../components/layouts/ClientLayout";
import { auth, database, firestore } from "../../firebaseConfig";
import { ref, onValue } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { FileText, Download, Eye, DollarSign, Phone, X } from "lucide-react";
import { API_BASE_URL } from "../../utils/apiBase";
const MPESA_PHONE_REGEX = /^254\d{9}$/;
const MPESA_MAX_AMOUNT = 150000;

/**
 * ClientInvoices Component
 * 
 * Displays all invoices for the client across all companies they're connected to.
 * Invoices are fetched from Firebase Realtime Database at:
 * Companies/{companyName}/ClientInvoices/{clientUsername}/invoices/{invoiceId}
 */

const DEFAULT_INVOICE_DATE = new Date("1970-01-01");

const ClientInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [connectedCompanies, setConnectedCompanies] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [companyClientMap, setCompanyClientMap] = useState({});
  const [companyPaybills, setCompanyPaybills] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentContext, setPaymentContext] = useState(null);

  const [user, setUser] = useState(null);

  // Set up auth listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Load client profile and connected companies
  useEffect(() => {
    if (!user) return;

    const loadClientData = async () => {
      try {
        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (!userDoc.exists()) return;

        const data = userDoc.data();
        setClientData(data);

        // Get list of companies the client is connected to
        const companies = data.connectedCompanies || [];
        setConnectedCompanies(companies);

        const mergedMap = {
          ...(data.client || {}),
          ...(data.clientSnapshots || {}),
        };

        setCompanyClientMap(mergedMap);
      } catch (error) {
        console.error("Error loading client data:", error);
        toast.error("Failed to load profile");
      }
    };

    loadClientData();
  }, [user]);

  const getClientUsername = useCallback(
    (companyName) => {
      const mappedClient = companyClientMap[companyName];
      if (mappedClient) return mappedClient;

      return (
        clientData?.OtherName ||
        clientData?.otherName ||
        clientData?.username ||
        user?.uid
      );
    },
    [companyClientMap, clientData, user?.uid]
  );

  const getInvoiceDate = useCallback(
    (invoice) => {
      if (!invoice || !invoice.invoiceDate) return DEFAULT_INVOICE_DATE;

      const parsed = new Date(invoice.invoiceDate);
      return Number.isNaN(parsed.getTime()) ? DEFAULT_INVOICE_DATE : parsed;
    },
    []
  );

  // Load paybill/account numbers for connected companies (used by STK push modal)
  useEffect(() => {
    if (!clientData || connectedCompanies.length === 0) return;

    const unsubscribers = [];

    connectedCompanies.forEach((companyName) => {
      const clientUsername = getClientUsername(companyName);
      if (!clientUsername) return;

      const clientRef = ref(
        database,
        `Companies/${companyName}/ValidatedClients/${clientUsername}`
      );

      const unsubscribe = onValue(clientRef, (snapshot) => {
        if (!snapshot.exists()) return;
        const data = snapshot.val();
        const paybill = data?.PaybillNumber || data?.paybillNumber;
        if (!paybill) return;

        const normalized = String(paybill).trim();
        setCompanyPaybills((prev) =>
          prev[companyName] === normalized
            ? prev
            : { ...prev, [companyName]: normalized }
        );
      });

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [clientData, connectedCompanies, getClientUsername]);

  // Load invoices from all connected companies
  useEffect(() => {
    if (!clientData || connectedCompanies.length === 0) {
      setInvoices([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const companyInvoiceMap = {};
    const processedCompanies = new Set();
    const unsubscribers = [];

    const updateAggregatedInvoices = () => {
      const aggregated = Object.values(companyInvoiceMap).flat();
      aggregated.sort((a, b) => getInvoiceDate(b) - getInvoiceDate(a));
      setInvoices(aggregated);

      if (processedCompanies.size === connectedCompanies.length) {
        setLoading(false);
      }
    };

    connectedCompanies.forEach((companyName) => {
      const clientUsername = getClientUsername(companyName);

      if (!clientUsername) {
        processedCompanies.add(companyName);
        companyInvoiceMap[companyName] = [];
        updateAggregatedInvoices();
        return;
      }

      const invoicesRef = ref(
        database,
        `Companies/${companyName}/ClientInvoices/${clientUsername}/invoices`
      );

      const unsubscribe = onValue(
        invoicesRef,
        (snapshot) => {
          processedCompanies.add(companyName);

          if (snapshot.exists()) {
            const invoicesData = snapshot.val();
            companyInvoiceMap[companyName] = Object.entries(invoicesData).map(
              ([id, invoice]) => ({
                id,
                ...invoice,
                companyName,
              })
            );
          } else {
            companyInvoiceMap[companyName] = [];
          }

          updateAggregatedInvoices();
        },
        (error) => {
          console.error(`Error loading invoices from ${companyName}:`, error);
          processedCompanies.add(companyName);
          if (!companyInvoiceMap[companyName]) {
            companyInvoiceMap[companyName] = [];
          }
          updateAggregatedInvoices();
        }
      );

      unsubscribers.push(unsubscribe);
    });

    // Cleanup
    return () => {
      unsubscribers.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [clientData, connectedCompanies, getClientUsername, getInvoiceDate]);

  // Filter invoices by status
  const filteredInvoices = invoices.filter((invoice) => {
    if (selectedStatus === "all") return true;
    return invoice.status?.toLowerCase() === selectedStatus;
  });

  // Calculate summary stats
  const stats = {
    total: invoices.length,
    paid: invoices.filter((i) => i.status === "paid").length,
    unpaid: invoices.filter((i) => i.status === "unpaid").length,
    overdue: invoices.filter((i) => i.status === "overdue").length,
    totalAmount: invoices.reduce((sum, i) => sum + Number(i.amount || 0), 0),
    unpaidAmount: invoices
      .filter((i) => i.status === "unpaid" || i.status === "overdue")
      .reduce((sum, i) => sum + Number(i.amount || 0), 0),
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "bg-green-100 text-green-800";
      case "unpaid":
        return "bg-yellow-100 text-yellow-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleViewInvoice = (invoice) => {
    // Generate a simple invoice view with transaction details
    // Escape HTML to prevent XSS
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    const invoiceNumber = escapeHtml(invoice.invoiceNumber || invoice.id || 'N/A');
    const companyName = escapeHtml(invoice.companyName || 'Unknown Company');
    const status = escapeHtml(invoice.status || 'Unknown');
    const description = invoice.description ? escapeHtml(invoice.description) : '';
    const amount = Number(invoice.amount || 0).toLocaleString();
    const invoiceDate = invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : '—';
    const dueDate = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '';
    
    const invoiceWindow = window.open('', '_blank');
    if (!invoiceWindow) {
      toast.error("Please allow popups to view invoices");
      return;
    }
    
    invoiceWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoiceNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
          .company { font-size: 24px; font-weight: bold; color: #f59e0b; }
          .invoice-info { margin: 20px 0; }
          .invoice-info div { margin: 5px 0; }
          .label { font-weight: bold; display: inline-block; width: 150px; }
          .amount { font-size: 32px; font-weight: bold; color: #f59e0b; margin: 20px 0; text-align: center; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; }
          .status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-weight: bold; }
          .status-paid { background: #d1fae5; color: #065f46; }
          .status-unpaid { background: #fef3c7; color: #92400e; }
          .status-overdue { background: #fee2e2; color: #991b1b; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company">${companyName}</div>
          <div style="font-size: 20px; margin-top: 10px;">INVOICE</div>
        </div>
        
        <div class="invoice-info">
          <div><span class="label">Invoice Number:</span> ${invoiceNumber}</div>
          <div><span class="label">Invoice Date:</span> ${invoiceDate}</div>
          ${dueDate ? `<div><span class="label">Due Date:</span> ${dueDate}</div>` : ''}
          <div><span class="label">Status:</span> 
            <span class="status status-${status.toLowerCase()}">${status}</span>
          </div>
        </div>
        
        ${description ? `<div style="margin: 20px 0;"><strong>Description:</strong><br/>${description}</div>` : ''}
        
        <div class="amount">
          AMOUNT: KES ${amount}
        </div>
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p style="font-size: 12px;">This is a computer-generated invoice from ${companyName}</p>
        </div>
      </body>
      </html>
    `);
    invoiceWindow.document.close();
    toast.success("Invoice opened in new window");
  };

  const handleDownloadInvoice = (invoice) => {
    // Generate CSV format download
    const csvContent = [
      ['Field', 'Value'],
      ['Invoice Number', invoice.invoiceNumber || invoice.id],
      ['Company', invoice.companyName],
      ['Amount', `KES ${Number(invoice.amount || 0).toLocaleString()}`],
      ['Status', invoice.status || 'Unknown'],
      ['Invoice Date', invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : '—'],
      ['Due Date', invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '—'],
      ['Description', invoice.description || 'N/A'],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `invoice_${invoice.invoiceNumber || invoice.id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Invoice downloaded as CSV");
  };

  const handlePayNow = (invoice) => {
    setPaymentContext({
      companyName: invoice.companyName,
      amount: Number(invoice.amount || 0),
      invoiceNumber: invoice.invoiceNumber || invoice.id,
    });
    setShowPaymentModal(true);
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading invoices...</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-yellow-700 flex items-center gap-2">
            <FileText className="w-8 h-8" />
            Invoices
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            View and manage your invoices
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Invoices" value={stats.total} color="bg-blue-100 text-blue-700" />
          <StatCard label="Paid" value={stats.paid} color="bg-green-100 text-green-700" />
          <StatCard label="Unpaid" value={stats.unpaid} color="bg-yellow-100 text-yellow-700" />
          <StatCard label="Overdue" value={stats.overdue} color="bg-red-100 text-red-700" />
        </div>

        {/* Amount Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-r from-green-100 to-green-50 rounded-xl p-4 shadow">
            <p className="text-sm text-green-700 font-medium">Total Invoiced</p>
            <p className="text-3xl font-bold text-green-800 mt-1">
              KES {stats.totalAmount.toLocaleString()}
            </p>
          </div>
          <div className="bg-gradient-to-r from-yellow-100 to-yellow-50 rounded-xl p-4 shadow">
            <p className="text-sm text-yellow-700 font-medium">Outstanding Amount</p>
            <p className="text-3xl font-bold text-yellow-800 mt-1">
              KES {stats.unpaidAmount.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b pb-2">
          <FilterButton
            label="All"
            active={selectedStatus === "all"}
            onClick={() => setSelectedStatus("all")}
          />
          <FilterButton
            label="Paid"
            active={selectedStatus === "paid"}
            onClick={() => setSelectedStatus("paid")}
          />
          <FilterButton
            label="Unpaid"
            active={selectedStatus === "unpaid"}
            onClick={() => setSelectedStatus("unpaid")}
          />
          <FilterButton
            label="Overdue"
            active={selectedStatus === "overdue"}
            onClick={() => setSelectedStatus("overdue")}
          />
        </div>

        {/* No connected companies */}
        {connectedCompanies.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <FileText className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              No Companies Connected
            </h3>
            <p className="text-yellow-700 mb-4">
              Connect to companies to view invoices
            </p>
            <a
              href="/client/settings"
              className="inline-block px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Go to Settings
            </a>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              No Invoices Found
            </h3>
            <p className="text-gray-500">
              {selectedStatus === "all"
                ? "You have no invoices yet"
                : `No ${selectedStatus} invoices found`}
            </p>
          </div>
        ) : (
          /* Invoices List */
          <div className="space-y-3">
            {filteredInvoices.map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                getStatusColor={getStatusColor}
                onView={handleViewInvoice}
                onDownload={handleDownloadInvoice}
                onPay={handlePayNow}
              />
            ))}
          </div>
        )}
      </div>

      {showPaymentModal && paymentContext && (
        <MpesaPaymentModal
          open={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentContext(null);
          }}
          companyName={paymentContext.companyName}
          paybill={companyPaybills[paymentContext.companyName]}
          defaultAmount={paymentContext.amount}
          invoiceNumber={paymentContext.invoiceNumber}
        />
      )}
    </ClientLayout>
  );
};

// Helper Components
const StatCard = ({ label, value, color }) => (
  <div className={`rounded-xl p-4 shadow ${color}`}>
    <p className="text-sm font-medium">{label}</p>
    <p className="text-2xl font-bold mt-1">{value}</p>
  </div>
);

const FilterButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg font-medium transition ${
      active
        ? "bg-yellow-600 text-white"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    }`}
  >
    {label}
  </button>
);

const InvoiceCard = ({ invoice, getStatusColor, onView, onDownload, onPay }) => (
  <div className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
    <div className="flex justify-between items-start mb-3">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-800">
            Invoice #{invoice.invoiceNumber || invoice.id}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
            {invoice.status || "Unknown"}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Company: <span className="font-medium">{invoice.companyName}</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Issued: {invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString() : "—"}
        </p>
        {invoice.dueDate && (
          <p className="text-xs text-gray-400">
            Due: {new Date(invoice.dueDate).toLocaleDateString()}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="text-xl font-bold text-yellow-700">
          KES {Number(invoice.amount || 0).toLocaleString()}
        </p>
      </div>
    </div>

    {/* Invoice Description */}
    {invoice.description && (
      <p className="text-sm text-gray-600 mb-3">{invoice.description}</p>
    )}

    {/* Action Buttons */}
    <div className="flex gap-2 pt-3 border-t">
      <button
        onClick={() => onView(invoice)}
        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
      >
        <Eye className="w-4 h-4" />
        View
      </button>
      <button
        onClick={() => onDownload(invoice)}
        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
      >
        <Download className="w-4 h-4" />
        Download
      </button>
      {(invoice.status === "unpaid" || invoice.status === "overdue") && (
        <button
          onClick={() => onPay(invoice)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
        >
          <DollarSign className="w-4 h-4" />
          Pay Now
        </button>
      )}
    </div>
  </div>
);

const MpesaPaymentModal = ({ open, onClose, companyName, paybill, defaultAmount, invoiceNumber }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [amount, setAmount] = useState(defaultAmount ? String(defaultAmount) : "");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    setAmount(defaultAmount ? String(defaultAmount) : "");
  }, [defaultAmount]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber || !amount) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!paybill) {
      toast.error("Paybill number not found for this company");
      return;
    }

    const trimmedPhone = phoneNumber.trim();
    if (!MPESA_PHONE_REGEX.test(trimmedPhone)) {
      toast.error("Enter a valid phone number in 254XXXXXXXXX format");
      return;
    }

    const numericAmount = Math.round(Number(amount));
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }

    if (numericAmount > MPESA_MAX_AMOUNT) {
      toast.error(`Amount exceeds the maximum allowed (KES ${MPESA_MAX_AMOUNT.toLocaleString()})`);
      return;
    }

    const sanitizeText = (value) => String(value || "").replace(/[<>]/g, "").trim();
    const safeInvoice = sanitizeText(invoiceNumber);
    const safeCompany = sanitizeText(companyName);

    setProcessing(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Please login to continue");
        setProcessing(false);
        return;
      }

      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/api/mpesa/stk-push/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          phone_number: trimmedPhone,
          amount: numericAmount,
          account_reference: paybill,
          transaction_desc: safeInvoice
            ? `Payment for invoice ${safeInvoice}`
            : `Payment to ${safeCompany}`,
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        toast.error("Unexpected response from payment service");
        return;
      }

      if (!response.ok) {
        toast.error(data?.error || "Failed to initiate payment");
        return;
      }

      if (data.success) {
        toast.success("Payment request sent! Please check your phone to complete the payment.");
        onClose();
      } else {
        toast.error(data.error || "Failed to initiate payment");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mpesa-payment-title"
      >
        <div className="flex justify-between items-center mb-6">
          <h2
            id="mpesa-payment-title"
            className="text-2xl font-bold text-gray-800 flex items-center gap-2"
          >
            <Phone className="w-6 h-6 text-yellow-600" />
            M-Pesa Payment
          </h2>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-gray-600 transition"
            aria-label="Close payment modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-yellow-800 mb-1">Paying to:</p>
          <p className="text-lg font-bold text-yellow-900">{companyName}</p>
          <div className="mt-3 space-y-2">
            {paybill ? (
              <div className="bg-white rounded px-3 py-2 border border-yellow-300">
                <p className="text-xs text-gray-600">Your Paybill / Account Number:</p>
                <p className="text-lg font-mono font-bold text-yellow-800">{paybill}</p>
                <p className="text-xs text-gray-500 mt-1">
                  This identifies you for payments to {companyName}.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded px-3 py-2 border border-yellow-300">
                <p className="text-xs text-gray-600">Paybill not available yet.</p>
                <p className="text-sm text-gray-700">
                  Please try again in a moment or contact support.
                </p>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="254XXXXXXXXX"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={processing}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your M-Pesa registered phone number
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (KES)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="100"
              min="1"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={processing}
              required
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> You will receive an M-Pesa prompt on your phone.
              Enter your PIN to complete the payment. The payment will be credited to
              your account with {companyName}.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4" />
                  Send Payment Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientInvoices;
